import {
    Box,
    Button,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    makeStyles,
} from "@material-ui/core";
import { ArrowForward } from "@material-ui/icons";
import { useHistory } from "react-router-dom";
import { currencyFormatter } from "../constants";

export type OutstandingClient = {
    id: number;
    balance: number;
    client?: {
        id?: number;
        employee?: {
            name?: string;
            phone?: string;
            city?: { name?: string };
        };
    };
};

const useStyles = makeStyles((theme) => ({
    paper: {
        borderRadius: 12,
        border: `1px solid ${theme.palette.divider}`,
        overflow: "hidden",
        height: "100%",
    },
    header: {
        padding: theme.spacing(2, 2.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    headerText: {
        flex: 1,
        minWidth: 200,
    },
    tableHead: {
        background: theme.palette.common.black,
        "& th": {
            color: theme.palette.common.white,
            fontWeight: 700,
            fontSize: "0.8rem",
            whiteSpace: "nowrap",
        },
    },
    row: {
        "&:hover": {
            backgroundColor: theme.palette.action.hover,
        },
    },
    rank: {
        fontWeight: 700,
        color: "#fcca4a",
        minWidth: 28,
    },
    balance: {
        fontWeight: 700,
        color: "#c62828",
        whiteSpace: "nowrap",
    },
    empty: {
        padding: theme.spacing(4),
        textAlign: "center",
        color: theme.palette.text.secondary,
    },
    muted: {
        color: theme.palette.text.secondary,
    },
}));

type Props = {
    clients: OutstandingClient[];
};

const TopOutstandingClients = ({ clients }: Props) => {
    const classes = useStyles();
    const history = useHistory();

    return (
        <Paper className={classes.paper} elevation={0}>
            <Box className={classes.header}>
                <Box className={classes.headerText}>
                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                        Top Outstanding Balances
                    </Typography>
                    <Typography variant="body2" className={classes.muted}>
                        Clients with the highest amounts due
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    endIcon={<ArrowForward />}
                    onClick={() => history.push("/payment/trial-balance")}
                >
                    View Trial Balance
                </Button>
            </Box>

            {clients.length === 0 ? (
                <Typography className={classes.empty}>
                    No clients with outstanding balance
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead className={classes.tableHead}>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Client</TableCell>
                                <TableCell>Phone</TableCell>
                                <TableCell>City</TableCell>
                                <TableCell align="right">
                                    Outstanding
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {clients.map((row, index) => {
                                const outstanding = Math.abs(
                                    Number(row.balance) || 0
                                );

                                return (
                                    <TableRow
                                        key={row.id}
                                        className={classes.row}
                                    >
                                        <TableCell className={classes.rank}>
                                            {index + 1}
                                        </TableCell>
                                        <TableCell
                                            style={{ whiteSpace: "nowrap" }}
                                        >
                                            {row.client?.employee?.name || "—"}
                                        </TableCell>
                                        <TableCell
                                            style={{ whiteSpace: "nowrap" }}
                                        >
                                            {row.client?.employee?.phone || (
                                                <span className={classes.muted}>
                                                    N/A
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {row.client?.employee?.city
                                                ?.name || (
                                                <span className={classes.muted}>
                                                    N/A
                                                </span>
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            className={classes.balance}
                                        >
                                            {currencyFormatter(outstanding)}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Paper>
    );
};

export default TopOutstandingClients;
